import { FormControlLabel, Radio, Stack, Typography } from '@mui/material'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'
import type { Distributor } from '../demo'

interface Props {
  distributor: Distributor
  selected: boolean
}

/**
 * One distributor, as a radio.
 *
 * The design draws a clickable card with a hand-built radio dot, which is a
 * `<div>` with an `onClick`: no focus, no role, no arrow keys. This is the same
 * picture over a real `<input type="radio">`, so the browser gives roving focus
 * and arrow-key navigation inside the group for free, and the whole card is the
 * label — clicking anywhere in it still selects.
 *
 * `disableTypography` matters: the default wraps the label in a `<span>`, and
 * this label has block-level children. Everything below is therefore explicitly
 * inline-level, nested inside the `<label>` FormControlLabel renders.
 *
 * Nothing here is real. Distributors have no endpoint — see `demo/fixtures.ts`.
 */
export const DistributorCard = ({ distributor, selected }: Props) => (
  <FormControlLabel
    value={distributor.id}
    labelPlacement="start"
    disableTypography
    control={<Radio />}
    label={
      <Stack component="span" direction="row" spacing={1.75} sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          component="span"
          alignItems="center"
          justifyContent="center"
          aria-hidden
          sx={{
            width: 44,
            height: 44,
            flex: 'none',
            borderRadius: `${YALO_RADIUS.md}px`,
            bgcolor: distributor.tint,
            color: YALO_SEMANTIC.fg2,
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {distributor.initials}
        </Stack>
        <Stack component="span" spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography component="span" sx={{ fontSize: 15.5, fontWeight: 600 }}>
            {distributor.name}
          </Typography>
          <Typography component="span" variant="body2" color="text.secondary">
            Entrega {distributor.deliveryDaysLabel}
          </Typography>
          <Typography component="span" variant="body2" sx={{ color: YALO_SEMANTIC.fg3 }}>
            {distributor.meta}
          </Typography>
        </Stack>
      </Stack>
    }
    sx={{
      m: 0,
      width: '100%',
      alignItems: 'center',
      gap: 1,
      p: 2,
      bgcolor: YALO_SEMANTIC.bgSurface,
      border: '1.5px solid',
      borderColor: selected ? 'primary.main' : YALO_SEMANTIC.borderSubtle,
      borderRadius: `${YALO_RADIUS.lg}px`,
      boxShadow: YALO_SHADOW.xs,
    }}
  />
)
